import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  useMediaQuery,
  useTheme
} from "@mui/material";
import {
  Assignment as AssignmentIcon,
} from "@mui/icons-material";
import AgendaHeader from "../../../molecules/multi-agenda/AgendaHeader";
import { useNavigate } from "react-router-dom";
import { AppRoutes } from "../../../constants";
import SkeletonLoader from "../../../atoms/project-details/SkeletonLoader";
import { TAgendaSectionProps } from "../type";
import useAgendaData from "../../../molecules/multi-agenda/hooks/usePeAgenda";
import useAgendaNavigation from "../../../molecules/multi-agenda/hooks/useAgendaStateNavigation";
import MobileAgendaAccordion from "../../../molecules/multi-agenda/MobileAgendaAccordion";
import AgendaDetails from "../../../molecules/multi-agenda/AgendaDetails";
import { AgendaList } from "../../../molecules/multi-agenda/AgendaList";


export default function AgendaSection({
  agendaId,
  invitationId,
  peId,
  goToNextStep,
  isAgenda,
  isInvitationAccepted,
  projectId,
  expertId
}: TAgendaSectionProps) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { peAgendaData, setPeAgendaData, loading, setLoading } = useAgendaData(
    peId,
    projectId,
  );
  const currentAgendaList = peAgendaData?.sentAgenda || [];
  const archivedAgendaList = [
    ...peAgendaData?.declineAgenda,
    ...peAgendaData?.respondedByAdminAgenda,
    ...peAgendaData?.respondedByExpertAgenda
  ];
  const {
    selectedAgendaIndex,
    setSelectedAgendaIndex,
    showArchived,
    expandedAccordion,
    setExpandedAccordion,
    toggleArchived,
  } = useAgendaNavigation(currentAgendaList.length);

  const displayedAgendaList = showArchived ? archivedAgendaList : currentAgendaList;
  const selectedAgenda = displayedAgendaList?.[selectedAgendaIndex];
  const handleNoSentAgendaExist = () => {
    if (peAgendaData.sentAgenda.length <= 1) {
      navigate(
        AppRoutes.CALENDER +
        "?id=" +
        projectId +
        "&expertId=" +
        expertId +
        "&new_user=true"
      );
    } 
  };

  if (loading) {
    return (
      <Box sx={{ p: 1 }}>
        <SkeletonLoader width="100%" height="500px" marginBottom="1.5em" />
      </Box>
    );
  }

  // Mobile View
  if (isMobile) {
    return (
      <Box sx={{ minHeight: '500px', display: 'flex', flexDirection: 'column' }}>
        <Card variant="outlined" sx={{ mb: 2, borderRadius: '10px' }}>
          <AgendaHeader showArchived={showArchived} onToggleArchived={toggleArchived} />
        </Card>

        {displayedAgendaList.length > 0 ? (
          <Box>
            {displayedAgendaList.map((agenda, index) => (
              <MobileAgendaAccordion
                key={agenda.id || index}
                agenda={agenda}
                index={index}
                isExpanded={expandedAccordion === `panel${index}`}
                onToggle={() => setExpandedAccordion(`panel${index}`)}
                showArchived={showArchived}
                peId={+peId}
                isAcceptInvitation={isInvitationAccepted}
                expertId={+expertId}
                projectId={+projectId}
                setPeAgendaData={setPeAgendaData}
                setLoading={setLoading}
                handleNoSentAgendaExist={handleNoSentAgendaExist}
              />
            ))}
          </Box>
        ) : (
          <Card variant="outlined" sx={{ borderRadius: '10px' }}>
            <CardContent>
              <EmptyAgendaState showArchived={showArchived} />
            </CardContent>
          </Card>
        )}
      </Box>
    );
  }

  // Desktop View
  return (
    <Box sx={{ height: '500px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Grid container spacing={0} sx={{ flex: 1, height: '100%', overflow: 'hidden', margin: 0, width: '100%' }}>
        {/* Left Sidebar */}
        <Grid item xs={12} md={4} sx={{ height: '100%', paddingLeft: '0 !important', paddingTop: '0 !important' }}>
          <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: '10px' }}>
            <AgendaHeader
              showArchived={showArchived}
              onToggleArchived={toggleArchived}
            />
            <AgendaList
              agendas={displayedAgendaList}
              selectedIndex={selectedAgendaIndex}
              onSelectAgenda={setSelectedAgendaIndex}
              showArchived={showArchived}
            />
          </Card>
        </Grid>

        {/* Right Section */}
        <Grid item xs={12} md={8} sx={{ height: '100%', paddingLeft: '16px !important', paddingTop: '0 !important' }}>
          <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: '10px' }}>
            {selectedAgenda && (
              <>
                <AgendaHeader
                  showArchived={showArchived}
                  onToggleArchived={toggleArchived}
                  selectedIndex={selectedAgendaIndex}
                  totalCount={displayedAgendaList.length}
                  showToggleBtn={false}
                />
                <AgendaDetails
                  agenda={selectedAgenda}
                  isAcceptInvitation={isInvitationAccepted}
                  showArchived={showArchived}
                  peId={+peId}
                  expertId={+expertId}
                  projectId={+projectId}
                  setPeAgendaData={setPeAgendaData}
                  handleNoSentAgendaExist={handleNoSentAgendaExist}
                />
              </>
            )}
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export const EmptyAgendaState: React.FC<{ showArchived: boolean }> = ({ showArchived }) => (
  <Box sx={{ p: 3, textAlign: 'center' }}>
    <AssignmentIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
    <Typography variant="body2" color="text.secondary">
      {showArchived ? "No archived agendas found" : "No active agendas available"}
    </Typography>
  </Box>
);